import React, { useEffect, useState, useMemo } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as offerActions from '../../actions/offers';
import { withRouter } from 'react-router';

import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import CanceledProductCardGrid from './CanceledProductCardGrid';
import '../Contact/Contact.scss';

const isMobile = window.innerWidth < 576;
const VIEW_STORAGE_KEY = 'canceledProductsViewMode';

interface Props {
    intl: any;
    history: any;
    offerActions: any;
    actions: any;
    offers: any;
    loading: boolean;
}

const CanceledProducts: React.FC<Props> = (props) => {
    const fm = (id: string, defaultMessage: string) => props.intl.formatMessage({ id, defaultMessage });

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        return (localStorage.getItem(VIEW_STORAGE_KEY) as 'table' | 'card') || 'table';
    });

    const columns: MikaColumn[] = useMemo(() => [
        {
            key: 'id',
            title: '#',
            sortable: true,
            width: '50px',
            render: (val: any) => <span style={{ color: '#94a3b8', fontSize: '11px' }}>{val}</span>
        },
        {
            key: 'client.name',
            title: fm('offer.list.column.client', 'Client'),
            sortable: true,
            render: (val: any) => val || '\u2014'
        },
        {
            key: 'project.name',
            title: fm('offer.list.column.project', 'Project'),
            render: (val: any) => val || '\u2014'
        },
        {
            key: 'article.name',
            title: fm('offer.list.column.product', 'Product'),
            sortable: true,
            render: (val: any) => val || '\u2014'
        },
        {
            key: 'quantity',
            title: fm('offer.list.column.quantity', 'Quantity'),
            width: '80px',
            render: (val: any) => val ?? '\u2014'
        }
    ], [props.intl]);

    const handleViewChange = (mode: 'table' | 'card') => {
        setViewMode(mode);
        localStorage.setItem(VIEW_STORAGE_KEY, mode);
    };

    const effectiveView = isMobile ? 'table' : viewMode;

    useEffect(() => {
        props.offerActions.getCanceledProducts(currentPage, perPage, searchTerm);
    }, [currentPage, perPage, searchTerm]);

    const data = props.offers?.canceledProducts || [];
    const pagination = props.offers?.canceledProductsPagination;

    const handleExport = () => {
        const header = [
            fm('offer.list.column.client', 'Client'),
            fm('offer.list.column.project', 'Project'),
            fm('offer.list.column.product', 'Product'),
            fm('offer.list.column.quantity', 'Quantity'),
        ];
        const rows = data.map((r: any) => [
            r.client?.name || '',
            r.project?.name || '',
            r.article?.name || '',
            r.quantity || ''
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
        const csv = [header.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${fm('offer.canceled.exportFilename', 'canceled-products')}.csv`;
        link.click();
    };

    return (
        <div className="clp-page">
            {!isMobile && (
                <MikaPageHeader
                    title={fm('offer.canceled.title', 'Canceled products')}
                    subtitle={pagination ? `${pagination.total} total` : undefined}
                />
            )}

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input" placeholder={fm('offer.canceled.searchPlaceholder', 'Search products...')}
                        value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => { setSearchTerm(''); setCurrentPage(1); }} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    {!isMobile && <MikaViewToggle mode={viewMode} onChange={handleViewChange} />}
                    <button className="clp-export-btn" onClick={handleExport} title={fm('offer.canceled.exportCsv', 'Export CSV')} type="button">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                    </button>
                </div>
            </div>

            {effectiveView === 'table'
                ? <MikaTable columns={columns} data={data} loading={props.loading} />
                : <CanceledProductCardGrid data={data} />
            }

            <PaginationComponent
                currentPage={currentPage} perPage={perPage} pagination={pagination}
                onPageChange={(p) => setCurrentPage(p)} onPerPageChange={(v) => { setPerPage(v); setCurrentPage(1); }}
                label={fm('offer.canceled.records', 'products')}
            />
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        offers: state.offers
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        offerActions: bindActionCreators(offerActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch)
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(CanceledProducts as any)));
